import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    ContactSearchHitFragmentFragment,
    ContactSearchHitType,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { navigate } from "store/actions";
import { ContactSearchHitDisplay } from "./contact-search-hit-display";

interface ContactSearchHitsLinkProps {
    hits: ContactSearchHitFragmentFragment[];
}

const typeToRelevanceScore: Record<ContactSearchHitType, number> = {
    [ContactSearchHitType.Client]: 100,
    [ContactSearchHitType.BestContact]: 90,
    [ContactSearchHitType.ClientContact]: 80,
    [ContactSearchHitType.CommunityContact]: 70,
    [ContactSearchHitType.ReferenceContact]: 60,
    [ContactSearchHitType.Community]: 50,
    [ContactSearchHitType.ReferenceBusiness]: 40,
    [ContactSearchHitType.HomeCareClient]: 100,
    [ContactSearchHitType.HomeCareBestContact]: 90,
    [ContactSearchHitType.HomeCareClientContact]: 80,
};

export const ContactSearchHitsLink: React.FC<ContactSearchHitsLinkProps> = props => {
    const { hits } = props;

    const [showPanel, setShowPanel] = useState(false);

    const handleLinkClick = useCallback(() => {
        if (hits.length > 1) {
            setShowPanel(true);
        } else {
            navigate(hits[0].appLink);
        }
    }, [hits]);

    const handleItemClick = useCallback((hit: ContactSearchHitFragmentFragment) => {
        navigate(hit.appLink);
    }, []);

    const hidePanel = useCallback(() => {
        setShowPanel(false);
    }, []);

    const sortedHits = useMemo(() => {
        return hits.sort((a, b) => {
            const diff = typeToRelevanceScore[b.type] - typeToRelevanceScore[a.type];
            if (diff === 0) {
                if (a.contact && b.contact) {
                    return (a.contact.name || "").localeCompare(b.contact.name || "");
                }
                if (a.client && b.client) {
                    return (a.client.name || "").localeCompare(b.client.name || "");
                }
                if (a.community && b.community) {
                    return (a.community.name || "").localeCompare(b.community.name || "");
                }

                return 0;
            }
            return diff;
        });
    }, [hits]);

    const mostRelevantHit = sortedHits[0];

    if (!mostRelevantHit) {
        return null;
    }

    return (
        <div onClick={e => e.stopPropagation()}>
            <ContactSearchHitDisplay
                hit={mostRelevantHit}
                onClick={handleLinkClick}
                mode="mini"
            />
            <Panel
                type={PanelType.large}
                headerText="Choose linked record"
                isOpen={showPanel}
                onDismiss={hidePanel}>
                <div className="space-y-4">
                    <p>
                        This contact information is associated with multiple records.
                        Choose the record to navigate to.
                    </p>
                    <div className="flex flex-col gap-4">
                        {sortedHits.map(hit => (
                            <ContactSearchHitDisplay
                                mode="full"
                                key={hit.id}
                                hit={hit}
                                onClick={() => handleItemClick(hit)}
                            />
                        ))}
                    </div>
                </div>
            </Panel>
        </div>
    );
};
